import { Menu as MenuPrimitive } from "@base-ui/react/menu";
import { Check, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { cn, resolveClassName } from "./cn";

export const Menu = MenuPrimitive.Root;
export const MenuTrigger = MenuPrimitive.Trigger;
export const MenuGroup = MenuPrimitive.Group;

export function MenuGroupLabel({ className, ...props }: MenuPrimitive.GroupLabel.Props) {
  return (
    <MenuPrimitive.GroupLabel
      className={resolveClassName(
        className,
        "px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--muted-2)]",
      )}
      {...props}
    />
  );
}

export interface MenuPopupProps extends MenuPrimitive.Popup.Props {
  readonly side?: MenuPrimitive.Positioner.Props["side"];
  readonly sideOffset?: MenuPrimitive.Positioner.Props["sideOffset"];
  readonly align?: MenuPrimitive.Positioner.Props["align"];
  readonly alignOffset?: MenuPrimitive.Positioner.Props["alignOffset"];
  readonly popupClassName?: string;
}

export function MenuPopup({
  className,
  popupClassName,
  children,
  side = "bottom",
  sideOffset = 4,
  align = "start",
  alignOffset = 0,
  ...props
}: MenuPopupProps) {
  const defaultClasses = cn(
    "dropdown-glass min-w-44 overflow-hidden rounded-lg p-1 text-[var(--text)] shadow-[0_16px_36px_-16px_rgb(0_0_0/75%)] outline-none focus:outline-none",
    popupClassName,
  );

  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        className="z-[130] select-none"
      >
        <MenuPrimitive.Popup className={resolveClassName(className, defaultClasses)} {...props}>
          {children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  );
}

export interface MenuItemProps extends MenuPrimitive.Item.Props {
  readonly icon?: ReactNode;
}

export function MenuItem({ className, children, icon, ...props }: MenuItemProps) {
  const defaultClasses =
    "relative flex h-8 min-w-0 cursor-pointer items-center gap-2 rounded-md px-2 text-xs font-medium text-[var(--text)] outline-none transition-colors select-none data-highlighted:bg-[var(--hover)] data-highlighted:text-[var(--text)] data-disabled:pointer-events-none data-disabled:opacity-50";

  return (
    <MenuPrimitive.Item className={resolveClassName(className, defaultClasses)} {...props}>
      {icon ? (
        <span className="shrink-0 text-[var(--muted)]" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </MenuPrimitive.Item>
  );
}

export function MenuCheckboxItem({
  className,
  children,
  ...props
}: MenuPrimitive.CheckboxItem.Props) {
  const defaultClasses =
    "relative flex h-8 min-w-0 cursor-pointer items-center justify-between gap-2 rounded-md px-2 text-xs font-medium text-[var(--text)] outline-none transition-colors select-none data-highlighted:bg-[var(--hover)] data-highlighted:text-[var(--text)] data-disabled:pointer-events-none data-disabled:opacity-50";

  return (
    <MenuPrimitive.CheckboxItem className={resolveClassName(className, defaultClasses)} {...props}>
      <span className="min-w-0 flex-1 truncate">{children}</span>
      <MenuPrimitive.CheckboxItemIndicator className="shrink-0 text-[var(--accent-text)]">
        <Check size={14} />
      </MenuPrimitive.CheckboxItemIndicator>
    </MenuPrimitive.CheckboxItem>
  );
}

export function MenuSeparator({ className, ...props }: MenuPrimitive.Separator.Props) {
  return (
    <MenuPrimitive.Separator
      className={resolveClassName(className, "my-1 h-px bg-[var(--border)]")}
      {...props}
    />
  );
}

export const MenuSubmenu = MenuPrimitive.SubmenuRoot;

export function MenuSubmenuTrigger({
  className,
  children,
  ...props
}: MenuPrimitive.SubmenuTrigger.Props) {
  const defaultClasses =
    "relative flex h-8 min-w-0 cursor-pointer items-center justify-between gap-2 rounded-md px-2 text-xs font-medium text-[var(--text)] outline-none transition-colors select-none data-highlighted:bg-[var(--hover)] data-highlighted:text-[var(--text)] data-disabled:pointer-events-none data-disabled:opacity-50";

  return (
    <MenuPrimitive.SubmenuTrigger
      className={resolveClassName(className, defaultClasses)}
      {...props}
    >
      <span className="min-w-0 flex-1 truncate">{children}</span>
      <ChevronRight size={14} className="shrink-0 text-[var(--muted)]" />
    </MenuPrimitive.SubmenuTrigger>
  );
}
